import React, { useMemo, useState, useId, useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import PropTypes from 'prop-types';
import { authFetch, API_BASE } from '../auth';
import './StraightPurchase.css';
import TickerAutocomplete from '../components/TickerAutocomplete';
import TickerIcon from '../components/TickerIcon';
import TickerLabel from '../components/TickerLabel';

const POLL_MS = 5000;

function parseTickers(raw) {
  return raw.split(',').map((t) => t.trim().toUpperCase()).filter(Boolean);
}

function createEmptyTicker() {
  return {
    direction: '',
    strike_mode: 'pct',
    strike_pct: '',
    exact_strike: '',
    dollar_dev: '',
    premium_mode: 'pct',
    premium_pct: '',
    max_amount: '',
    expiry_date: '',
  };
}

function safeId(str) {
  return String(str).replace(/[^a-z0-9_-]/gi, '_');
}

function validateForm(formData) {
  const errs = new Set();
  Object.entries(formData).forEach(([ticker, d]) => {
    const key = (field) => `${ticker}.${field}`;
    if (!d.direction) errs.add(key('direction'));

    const mode = d.strike_mode || 'pct';
    if (mode === 'exact' && !d.exact_strike) errs.add(key('exact_strike'));
    if (mode === 'pct' && !d.strike_pct) errs.add(key('strike_pct'));
    if (mode === 'dollar' && !d.dollar_dev) errs.add(key('dollar_dev'));

    if (!d.premium_pct) errs.add(key('premium_pct'));
    if (!d.max_amount) errs.add(key('max_amount'));
    if (!d.expiry_date) errs.add(key('expiry_date'));
  });
  return errs;
}

const STATUS_LABELS = {
  active: 'Active',
  filled: 'Filled',
  expired: 'Expired',
  cancelled: 'Cancelled',
  error: 'Error',
};

/* ---------- ActiveOrders ---------- */

function ActiveOrders({ orders, onCancel, onClear }) {
  if (orders.length === 0) return null;
  const hasCompleted = orders.some((o) => o.status !== 'active');
  return (
    <section className="sp__orders">
      <div className="sp__orders-head">
        <h2 className="sp__orders-title">Active Orders</h2>
        {hasCompleted && (
          <button type="button" className="sp__ghost-btn" onClick={onClear}>
            Clear completed
          </button>
        )}
      </div>
      <div className="sp__order-list">
        {orders.map((o) => (
          <div className={`sp__order-row sp__order-row--${o.status}`} key={o.id}>
            <div className="sp__order-ticker">{o.ticker}</div>
            <div className={`sp__order-badge sp__order-badge--${o.status}`}>
              {STATUS_LABELS[o.status] || o.status}
            </div>
            <div className="sp__order-msg">{o.message}</div>
            <div className="sp__order-time">{o.created_at}</div>
            {o.status === 'active' && (
              <button
                type="button"
                className="sp__ghost-btn sp__ghost-btn--sm"
                onClick={() => onCancel(o.id)}
              >
                Cancel
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

ActiveOrders.propTypes = {
  orders: PropTypes.array.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

/* ---------- main component ---------- */

export default function StraightPurchase() {
  const uid = useId();
  const [tickerInput, setTickerInput] = useState('');
  const [formData, setFormData] = useState({});
  const [status, setStatus] = useState({ state: 'idle', message: '' });
  const [errors, setErrors] = useState(new Set());
  const [orders, setOrders] = useState([]);
  const pollRef = useRef(null);

  const tickers = useMemo(() => parseTickers(tickerInput), [tickerInput]);
  const hasUnsavedData = tickers.length > 0;

  const hasError = useCallback(
    (ticker, field) => errors.has(`${ticker}.${field}`),
    [errors]
  );

  const blocker = useBlocker(hasUnsavedData);

  useEffect(() => {
    const handler = (e) => {
      if (hasUnsavedData) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedData]);

  // ── polling ──
  const fetchStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/straight-purchase/status/`);
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      }
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const handleCancel = async (orderId) => {
    try {
      await authFetch(`${API_BASE}/straight-purchase/cancel/${orderId}/`, { method: 'POST' });
      fetchStatus();
    } catch {
      /* silent */
    }
  };

  const handleClear = async () => {
    try {
      await authFetch(`${API_BASE}/straight-purchase/clear/`, { method: 'POST' });
      fetchStatus();
    } catch {
      /* silent */
    }
  };

  // ── form handlers ──

  const handleTickerChange = (raw) => {
    setTickerInput(raw);
    const nextTickers = parseTickers(raw);
    setFormData((prev) => {
      const next = {};
      nextTickers.forEach((t) => {
        next[t] = { ...createEmptyTicker(), ...(prev[t] || {}) };
      });
      return next;
    });
  };

  const removeTicker = (ticker) => {
    const nextTickers = tickers.filter((t) => t !== ticker);
    setTickerInput(nextTickers.join(', '));
    setFormData((prev) => {
      const next = { ...prev };
      delete next[ticker];
      return next;
    });
    setErrors((prev) => {
      const next = new Set(prev);
      for (const k of prev) {
        if (k.startsWith(`${ticker}.`)) next.delete(k);
      }
      return next;
    });
  };

  const handleFieldChange = (ticker, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [ticker]: { ...prev[ticker], [field]: value },
    }));
    const errKey = `${ticker}.${field}`;
    if (errors.has(errKey)) {
      setErrors((prev) => {
        const next = new Set(prev);
        next.delete(errKey);
        return next;
      });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validateForm(formData);
    setErrors(errs);
    if (errs.size > 0) {
      setStatus({
        state: 'error',
        message: `${errs.size} required field${errs.size > 1 ? 's' : ''} missing`,
      });
      return;
    }

    const payload = { data: {} };
    Object.entries(formData).forEach(([ticker, fields]) => {
      payload.data[ticker] = { ...fields };
    });

    setStatus({ state: 'loading', message: 'Submitting\u2026' });

    authFetch(`${API_BASE}/straight-purchase/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (!ok) throw new Error(data?.detail || data?.error || 'Request failed');
        const submitted = Object.keys(data.order_ids || {}).join(', ');
        setStatus({ state: 'success', message: `Submitted: ${submitted}` });
        fetchStatus();
      })
      .catch((err) => {
        setStatus({ state: 'error', message: err?.message || 'Error submitting strategy' });
      });
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-header__title">Straight Purchase</h2>
        <p className="page-header__subtitle">
          Buy calls or puts with flexible strike and premium selection.
        </p>
      </div>

      <ActiveOrders orders={orders} onCancel={handleCancel} onClear={handleClear} />

      <form onSubmit={handleSubmit} className="sp__form">
        <section className="sp__card">
          <div className="sp__card-head">
            <h2 className="sp__card-title">Tickers</h2>
            <div className="sp__meta">{tickers.length} ticker(s)</div>
          </div>

          <div className="sp__grid sp__grid--2">
            <div className="sp__field">
              <label className="label" htmlFor={`sp-${uid}-tickers`}>
                Tickers (comma-separated)
              </label>
              <div className="sp__field-hint">Example: AAPL, MSFT, SPY</div>
              <TickerAutocomplete
                id={`sp-${uid}-tickers`}
                className="input"
                value={tickerInput}
                onChange={handleTickerChange}
                placeholder="AAPL, MSFT"
              />
            </div>

            <div className="sp__chips" aria-label="Current tickers">
              {tickers.length === 0 ? (
                <div className="sp__hint-box">Add at least one ticker to begin.</div>
              ) : (
                tickers.map((t) => (
                  <button
                    key={t}
                    type="button"
                    className="sp__chip"
                    onClick={() => removeTicker(t)}
                    title={`Remove ${t}`}
                  >
                    <TickerIcon symbol={t} size={16} />
                    <span>{t}</span>
                    <span aria-hidden="true">&times;</span>
                  </button>
                ))
              )}
            </div>
          </div>
        </section>

        {Object.keys(formData).map((ticker) => {
          const tSafe = safeId(ticker);
          const d = formData[ticker];
          const mode = d.strike_mode || 'pct';

          return (
            <section
              className={`sp__ticker-card${d.direction === 'C' ? ' sp__ticker-card--call' : d.direction === 'P' ? ' sp__ticker-card--put' : ''}`}
              key={ticker}
            >
              <div className="sp__ticker-card-head">
                <div>
                  <div className="sp__ticker-name"><TickerLabel symbol={ticker} /></div>
                  <div className="sp__ticker-sub">Configure option purchase</div>
                </div>
                <button type="button" className="sp__ghost-btn" onClick={() => removeTicker(ticker)}>
                  Remove
                </button>
              </div>

              <div className="sp__grid sp__grid--3">
                {/* Direction */}
                <div className={`sp__field ${hasError(ticker, 'direction') ? 'sp__field--error' : ''}`}>
                  <label className="label" htmlFor={`sp-${uid}-${tSafe}-dir`}>
                    Direction <span className="sp__required">*</span>
                  </label>
                  <select
                    id={`sp-${uid}-${tSafe}-dir`}
                    className="select"
                    value={d.direction || ''}
                    onChange={(e) => handleFieldChange(ticker, 'direction', e.target.value)}
                  >
                    <option value="">Select&hellip;</option>
                    <option value="C">C (Call)</option>
                    <option value="P">P (Put)</option>
                  </select>
                </div>

                {/* Strike mode */}
                <div className="sp__field">
                  <label className="label" htmlFor={`sp-${uid}-${tSafe}-smode`}>
                    Strike mode
                  </label>
                  <select
                    id={`sp-${uid}-${tSafe}-smode`}
                    className="select"
                    value={mode}
                    onChange={(e) => handleFieldChange(ticker, 'strike_mode', e.target.value)}
                  >
                    <option value="pct">Strike %</option>
                    <option value="exact">Exact Strike</option>
                    <option value="dollar">$ Deviation</option>
                  </select>
                </div>

                {/* Strike value (adapts based on mode) */}
                {mode === 'exact' ? (
                  <div className={`sp__field ${hasError(ticker, 'exact_strike') ? 'sp__field--error' : ''}`}>
                    <label className="label" htmlFor={`sp-${uid}-${tSafe}-exact`}>
                      Exact strike <span className="sp__required">*</span>
                    </label>
                    <div className="sp__field-hint">Strike price (e.g., 185.00)</div>
                    <input
                      id={`sp-${uid}-${tSafe}-exact`}
                      className="input"
                      type="number"
                      step="0.01"
                      value={d.exact_strike || ''}
                      onChange={(e) => handleFieldChange(ticker, 'exact_strike', e.target.value)}
                    />
                  </div>
                ) : mode === 'dollar' ? (
                  <div className={`sp__field ${hasError(ticker, 'dollar_dev') ? 'sp__field--error' : ''}`}>
                    <label className="label" htmlFor={`sp-${uid}-${tSafe}-dollardev`}>
                      $ deviation <span className="sp__required">*</span>
                    </label>
                    <div className="sp__field-hint">Dollar deviation from current price</div>
                    <input
                      id={`sp-${uid}-${tSafe}-dollardev`}
                      className="input"
                      type="number"
                      step="0.01"
                      min="0"
                      value={d.dollar_dev || ''}
                      onChange={(e) => handleFieldChange(ticker, 'dollar_dev', e.target.value)}
                    />
                  </div>
                ) : (
                  <div className={`sp__field ${hasError(ticker, 'strike_pct') ? 'sp__field--error' : ''}`}>
                    <label className="label" htmlFor={`sp-${uid}-${tSafe}-spct`}>
                      Strike % from price <span className="sp__required">*</span>
                    </label>
                    <div className="sp__field-hint">e.g., 1.5 means 1.5% away</div>
                    <input
                      id={`sp-${uid}-${tSafe}-spct`}
                      className="input"
                      type="number"
                      step="0.01"
                      value={d.strike_pct || ''}
                      onChange={(e) => handleFieldChange(ticker, 'strike_pct', e.target.value)}
                    />
                  </div>
                )}

                {/* Premium mode */}
                <div className="sp__field">
                  <label className="label" htmlFor={`sp-${uid}-${tSafe}-pmode`}>
                    Premium mode
                  </label>
                  <select
                    id={`sp-${uid}-${tSafe}-pmode`}
                    className="select"
                    value={d.premium_mode || 'pct'}
                    onChange={(e) => handleFieldChange(ticker, 'premium_mode', e.target.value)}
                  >
                    <option value="pct">% of price</option>
                    <option value="dollar">$ limit</option>
                  </select>
                </div>

                {/* Premium value */}
                <div className={`sp__field ${hasError(ticker, 'premium_pct') ? 'sp__field--error' : ''}`}>
                  <label className="label" htmlFor={`sp-${uid}-${tSafe}-ppct`}>
                    {d.premium_mode === 'dollar' ? 'Premium $ cap' : 'Premium %'} <span className="sp__required">*</span>
                  </label>
                  <input
                    id={`sp-${uid}-${tSafe}-ppct`}
                    className="input"
                    type="number"
                    step="0.01"
                    value={d.premium_pct || ''}
                    onChange={(e) => handleFieldChange(ticker, 'premium_pct', e.target.value)}
                    placeholder={d.premium_mode === 'dollar' ? 'e.g. 1.20' : 'e.g. 0.5'}
                  />
                </div>

                {/* Max amount */}
                <div className={`sp__field ${hasError(ticker, 'max_amount') ? 'sp__field--error' : ''}`}>
                  <label className="label" htmlFor={`sp-${uid}-${tSafe}-amt`}>
                    Max amount ($) <span className="sp__required">*</span>
                  </label>
                  <input
                    id={`sp-${uid}-${tSafe}-amt`}
                    className="input"
                    type="number"
                    step="1"
                    value={d.max_amount || ''}
                    onChange={(e) => handleFieldChange(ticker, 'max_amount', e.target.value)}
                  />
                </div>

                {/* Expiry date */}
                <div className={`sp__field ${hasError(ticker, 'expiry_date') ? 'sp__field--error' : ''}`}>
                  <label className="label" htmlFor={`sp-${uid}-${tSafe}-exp`}>
                    Expiry date <span className="sp__required">*</span>
                  </label>
                  <input
                    id={`sp-${uid}-${tSafe}-exp`}
                    className="input"
                    type="date"
                    value={d.expiry_date || ''}
                    onChange={(e) => handleFieldChange(ticker, 'expiry_date', e.target.value)}
                  />
                </div>
              </div>
            </section>
          );
        })}

        <div className="action-bar">
          <div className={`action-bar__status ${status.state === 'success' ? 'action-bar__status--success' : status.state === 'error' ? 'action-bar__status--error' : ''}`}>
            {status.state === 'idle' ? 'Ready' : status.message}
          </div>
          <button
            className="btn btn--primary"
            type="submit"
            disabled={status.state === 'loading' || tickers.length === 0}
          >
            {status.state === 'loading' ? 'Submitting\u2026' : 'Run Strategy'}
          </button>
        </div>
      </form>

      {/* In-app navigation guard */}
      {blocker.state === 'blocked' && (
        <div
          className="pbModal__backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="sp-nav-title"
        >
          <div className="pbModal pbModal--sm">
            <div className="pbModal__header">
              <h2 className="pbModal__title" id="sp-nav-title">
                Leave page?
              </h2>
              <p className="pbModal__subtitle">
                You have unsaved order data. Leaving now will discard everything
                you&apos;ve entered.
              </p>
            </div>
            <div className="pbModal__footer">
              <button
                type="button"
                className="pbGhostBtn"
                onClick={() => blocker.reset()}
              >
                Stay on page
              </button>
              <button
                type="button"
                className="pbDangerBtn"
                onClick={() => blocker.proceed()}
              >
                Leave anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
